import { Navigate } from 'react-router-dom';
import LazyLoad from './LazyLoad';
import { staticMenuRoutes } from './router/staticMenuRoutes';
import { Routes } from './router/routes';
/**
 * 路由模式配置 
 * 'static'  - 静态模式：使用本地配置
 * 'dynamic' - 动态模式：从 API 获取（模拟） 
 *  'mixed'   - 混合模式：先加载静态，再获取动态合并
 */
export const ROUTE_MODE = 'mixed'; // 切换此值改变模式：'static' | 'dynamic' | 'mixed'

/**
 * 模拟从 API 获取的动态路由配置
 * 后期替换为真实的 API 请求
 * 混合模式下，返回的路由会与静态路由合并
 * 返回 null 表示没有动态路由更新
 */
const fetchDynamicRoutes = () => {
    const res = localStorage.getItem("menuRoutes");
    if (res) {
        try {
            return JSON.parse(res);
        } catch (error) {
            console.error('解析缓存路由失败:', error);
            return null;
        }
    }
    return null;
};

/**
 * 获取菜单路由配置
 * 根据 ROUTE_MODE 返回不同的配置
 * 
 * static - 返回静态配置
 * dynamic - 返回动态配置
 * mixed - 先返回静态，再获取动态合并
 */
export const getMenuRoutes = () => {
    if (ROUTE_MODE === 'dynamic') {
        try {
            const dynamicRoutes = fetchDynamicRoutes();
            // 如果缓存为空，返回静态配置
            return dynamicRoutes || staticMenuRoutes;
        } catch (error) {
            console.error('获取动态路由失败，使用静态配置:', error);
            return staticMenuRoutes;
        }
    }
    // static 和 mixed 模式都先返回静态配置
    return staticMenuRoutes;
};

// 获取动态路由增量更新（用于混合模式） 
export const getDynamicRoutesIncremental = () => {
    if (ROUTE_MODE === 'mixed') {
        try {
            const dynamicRoutes = fetchDynamicRoutes();
            return dynamicRoutes;
        } catch (error) {
            console.error('获取动态路由失败:', error);
            return null;
        }
    }
    return null;
};

// 同步获取静态路由（用于初始渲染）
export const menuRoutes = staticMenuRoutes;

// 统一处理 LazyLoad 
const processLazyLoad = (routes) => {
    return routes.map(route => {
        const processedRoute = { ...route };

        // 如果有 component，转换为 element
        if (route.component) {
            processedRoute.element = LazyLoad(route.component);
            delete processedRoute.component;
        }

        // 递归处理子路由
        if (route.children && route.children.length > 0) {
            processedRoute.children = processLazyLoad(route.children);
        }

        return processedRoute;
    });
};

// 合并静态路由和动态路由 
export const mergeRoutes = (staticRoutes, dynamicRoutes) => {
    if (!dynamicRoutes || dynamicRoutes.length === 0) {
        return staticRoutes;
    }

    // 创建静态路由的副本
    const merged = [...staticRoutes];

    // 合并动态路由
    dynamicRoutes.forEach(dynamicRoute => {
        // 检查是否已存在相同 name 的路由（优先使用 name 字段，其次使用 meta.name）
        const existingIndex = merged.findIndex(
            route => (route.name && route.name === dynamicRoute.name) ||
                (route.meta?.name && route.meta?.name === dynamicRoute.meta?.name)
        );

        if (existingIndex >= 0) {
            // 如果存在，合并子路由
            if (dynamicRoute.children && dynamicRoute.children.length > 0) {
                const existingRoute = merged[existingIndex];
                if (!existingRoute.children) {
                    existingRoute.children = [];
                }
                // 添加新的子路由（根据 name 或 meta.name 去重）
                dynamicRoute.children.forEach(child => {
                    const childExists = existingRoute.children.find(
                        c => (c.name && c.name === child.name) ||
                            (c.meta?.name && c.meta?.name === child.meta?.name)
                    );
                    if (!childExists) {
                        existingRoute.children.push(child);
                    }
                });
            }
        } else {
            // 如果不存在，添加新路由
            merged.push(dynamicRoute);
        }
    });

    return merged;
};

// 获取第一个有 component 的路由的完整路径
const getFirstPath = (routes, parentPath = '') => {
    for (const route of routes) {
        // 跳过隐藏路由
        if (route.meta?.hidden) {
            continue;
        }
        // 构建当前路由的完整路径
        const currentPath = parentPath === '' ? route.path : `${parentPath}/${route.path}`;

        // 如果当前路由有 component，返回完整路径
        if (route.component) {
            return currentPath;
        }

        // 如果有子路由，递归查找
        if (route.children && route.children.length > 0) {
            const childPath = getFirstPath(route.children, currentPath);
            if (childPath) {
                return childPath;
            }
        }
    }
    return null;
};

// 生成完整的路由配置 
export const generateRoutes = (menuRoutesConfig = staticMenuRoutes) => {
    // 处理菜单路由，添加 LazyLoad
    const processedMenuRoutes = processLazyLoad(menuRoutesConfig);

    // 处理子路由，移除 meta 信息（保留 element）
    const processChildren = (routes) => {
        return routes.map(route => {
            const { meta, children, ...rest } = route;
            if (children && children.length > 0) {
                return {
                    ...rest,
                    children: processChildren(children)
                };
            }
            return rest;
        });
    };

    // 获取第一个路径作为默认导航
    const firstPath = getFirstPath(menuRoutesConfig) || 'dashboard';

    return [
        {name:'登录', path: '/login', element: LazyLoad('/login/index') },
        { name:'404',path: '/404', element: LazyLoad('/NotFind/404') },
        {
            name:'首页',
            path: '/',
            element: LazyLoad('/home/index'),
            children: [
                { path: '', element: <Navigate to={firstPath} replace /> },
                ...processChildren(processedMenuRoutes)
            ]
        },
        ...Routes,
        { path: '/*', element: <Navigate to='/404' /> }
    ];
};

// 默认导出静态路由配置
const routes = generateRoutes();

function getFullRoutes() {
  try {
    // 动态模式：只用动态路由
    if (ROUTE_MODE === 'dynamic') {
      const dynamicRoutes = fetchDynamicRoutes();
      if (dynamicRoutes && dynamicRoutes.length > 0) {
        return generateRoutes(dynamicRoutes);
      }
      return generateRoutes(staticMenuRoutes);
    }

    // 混合模式：静态 + 动态合并
    if (ROUTE_MODE === 'mixed') {
      const dynamicRoutes = fetchDynamicRoutes();
      if (dynamicRoutes && dynamicRoutes.length > 0) {
        const mergedMenuRoutes = mergeRoutes(staticMenuRoutes, dynamicRoutes);
        return generateRoutes(mergedMenuRoutes);
      }
      return generateRoutes(staticMenuRoutes);
    }

    // 静态模式（默认）
    return generateRoutes(staticMenuRoutes);
  } catch (error) {
    console.error('获取完整路由配置失败，使用静态路由:', error);
    return generateRoutes(staticMenuRoutes);
  }
}
export function getRouteName(pathname, routesConfig) {
  const routes = routesConfig || getFullRoutes();

  // 规范化路径：去掉末尾斜杠，确保以 / 开头
  const normalizePath = (path) => {
    if (!path) return '/';
    if (!path.startsWith('/')) path = '/' + path;
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
    return path;
  };

  // 拼接路径（修复 // 问题）
  const joinPath = (parent, child) => {
    if (!child) return parent;
    if (child.startsWith('/')) return child;
    
    // 父路径是 / 时，直接 /child
    if (parent === '/' || parent === '') {
      return '/' + child;
    }
    
    // 正常拼接
    return parent.replace(/\/$/, '') + '/' + child;
  };

  // 递归查找
  const findName = (routeList, parentPath = '') => {
    for (const route of routeList) {
      if (!route || !route.path) continue;
      
      // 拼接完整路径
      const fullPath = normalizePath(joinPath(parentPath, route.path));
      const targetPath = normalizePath(pathname);
      
      // 精确匹配
      if (fullPath === targetPath) {
        return route.name || route.meta?.name || '';
      }
      
      // 递归查找子路由
      if (route.children && route.children.length > 0) {
        const childName = findName(route.children, fullPath);
        if (childName) return childName;
      }
    }
    return null;
  };

  const name = findName(routes);
  return name || '数据可视化平台';
}
export function getBreadcrumb(pathname, routesConfig) {
  const routes = routesConfig || getFullRoutes();
  const result = [];

  // 规范化路径
  const normalizePath = (path) => {
    if (!path) return '/';
    if (!path.startsWith('/')) path = '/' + path;
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
    return path;
  };

  // 拼接路径
  const joinPath = (parent, child) => {
    if (!child) return parent;
    if (child.startsWith('/')) return child;
    if (parent === '/' || parent === '') {
      return '/' + child;
    }
    return parent.replace(/\/$/, '') + '/' + child;
  };

  const findPath = (routeList, parentPath = '') => {
    for (const route of routeList) {
      if (!route || !route.path) continue;
      
      const fullPath = normalizePath(joinPath(parentPath, route.path));
      const targetPath = normalizePath(pathname);
      
      // 当前路由匹配，或是目标路径的前缀
      if (fullPath === targetPath || 
          (fullPath !== '/' && targetPath.startsWith(fullPath + '/'))) {
        result.push({
          path: fullPath,
          name: route.name || route.meta?.name || ''
        });
        
        if (route.children && route.children.length > 0) {
          findPath(route.children, fullPath);
        }
        return true;
      }
    }
    return false;
  };

  findPath(routes);
  return result;
}
export default routes;
